<template>
  <div class="hardware-config">
    <div class="config-header">
      <div class="title">
        <v-icon class="mr-3">mdi-monitor-multiple</v-icon>
        Hardware Outputs
      </div>
      <v-spacer />
      <HelpButton mdx-name="HardwareOutputs" />
    </div>
    <v-divider class="mt-3" />

    <div class="config-page-body hardware-body">
      <div class="canvas-column">
        <div class="section-heading">Canvas Preview</div>
        <div
          class="canvas-wrapper"
          ref="canvasContainer">
          <div
            class="canvas-area"
            v-if="!loading"
            :style="{
              width: `${previewWidth}px`,
              height: `${previewHeight}px`,
            }">
            <VueDraggableResizable
              v-for="output in outputs"
              :key="output.tempId"
              :parent="true"
              :x="output.left * scale"
              :y="output.top * scale"
              :w="Math.max(output.width * scale, 20)"
              :h="Math.max(output.height * scale, 20)"
              :draggable="selectedId === output.tempId"
              :resizable="selectedId === output.tempId"
              :min-width="30"
              :min-height="30"
              class="hardware-output-draggable"
              :class="{
                'hardware-output-draggable--selected':
                  selectedId === output.tempId,
              }"
              class-name-handle="frameitem"
              style="position: absolute; touch-action: none"
              @pointerdown.capture="selectOutput(output.tempId)"
              @dragging="onDrag"
              @resizing="onResize">
              <div
                class="output-chip"
                :class="{
                  'output-chip--selected': selectedId === output.tempId,
                }"
                @click.stop="selectOutput(output.tempId)">
                <div class="output-name">{{ output.name }}</div>
                <div class="output-sub">
                  GPU {{ output.gpu_index + 1 }} · Display
                  {{ output.display_index + 1 }}
                </div>
              </div>
            </VueDraggableResizable>
            <div
              v-if="outputs.length === 0"
              class="empty-state">
              <v-icon
                size="32"
                class="mb-2">
                mdi-monitor-off
              </v-icon>
              No hardware outputs configured yet
            </div>
          </div>
          <div
            class="canvas-loading"
            v-else>
            <v-progress-circular
              indeterminate
              class="mr-2" />
            Loading outputs…
          </div>
        </div>
      </div>

      <div class="sidebar-column">
        <div class="section-heading">Outputs</div>
        <v-btn
          class="mb-3"
          color="primary"
          variant="flat"
          prepend-icon="mdi-plus"
          @click="addOutput">
          Add Output
        </v-btn>
        <v-list
          class="outputs-list"
          density="compact">
          <v-list-item
            v-for="output in outputs"
            :key="output.tempId"
            :value="output.tempId"
            :class="{
              'outputs-list__item--selected': selectedId === output.tempId,
            }"
            @click="selectOutput(output.tempId)">
            <template #prepend>
              <v-avatar
                size="28"
                class="mr-3"
                color="primary"
                variant="tonal">
                <span>{{ outputLabel(output) }}</span>
              </v-avatar>
            </template>
            <v-list-item-title>{{ output.name }}</v-list-item-title>
            <v-list-item-subtitle>
              GPU {{ output.gpu_index + 1 }} · Display
              {{ output.display_index + 1 }}
            </v-list-item-subtitle>
            <template #append>
              <v-btn
                icon="mdi-delete"
                size="small"
                variant="text"
                color="error"
                @click.stop="removeOutput(output.tempId)" />
            </template>
          </v-list-item>
        </v-list>
        <div
          v-if="selectedOutput"
          class="details-card">
          <div class="section-heading">Selected Output</div>
          <v-select
            v-model.number="selectedOutput.gpu_index"
            label="GPU Index"
            :items="gpuOptions"
            item-title="title"
            item-value="value"
            density="comfortable"
            hide-details="auto" />
          <v-select
            v-model.number="selectedOutput.display_index"
            label="Display Index"
            :items="displayOptions"
            item-title="title"
            item-value="value"
            density="comfortable"
            hide-details="auto" />
          <v-text-field
            v-model.number="selectedOutput.width"
            label="Width (px)"
            type="number"
            min="1"
            density="comfortable"
            hide-details="auto" />
          <v-text-field
            v-model.number="selectedOutput.height"
            label="Height (px)"
            type="number"
            min="1"
            density="comfortable"
            hide-details="auto" />
          <v-text-field
            v-model.number="selectedOutput.left"
            label="Left"
            type="number"
            density="comfortable"
            hide-details="auto" />
          <v-text-field
            v-model.number="selectedOutput.top"
            label="Top"
            type="number"
            density="comfortable"
            hide-details="auto" />
          <v-select
            v-model="selectedOutput.fullscreen_mode"
            label="Output Mode"
            :items="fullscreenOptions"
            item-title="title"
            item-value="value"
            density="comfortable"
            hide-details="auto" />
        </div>
      </div>
    </div>

    <v-alert
      v-if="error"
      type="error"
      class="mt-4"
      variant="tonal">
      {{ error }}
    </v-alert>

    <v-divider class="mt-4" />
    <div class="config-footer">
      <v-spacer />
      <v-btn
        color="primary"
        variant="flat"
        :loading="saving"
        :disabled="saving"
        prepend-icon="mdi-content-save"
        @click="saveOutputs">
        Save
      </v-btn>
    </div>
  </div>
</template>
<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { deluge } from '@/stores/deluge.js';
import VueDraggableResizable from 'vue-draggable-resizable';
import HelpButton from '../Help-Hints/HelpButton.vue';
import {
  fetchHardwareOutputs,
  fetchHardwareCapabilities,
  saveHardwareOutputs,
} from '@/utils/hardwareOutputs.js';

const outputs = ref([]);
const loading = ref(true);
const saving = ref(false);
const error = ref('');
const selectedId = ref(null);
const canvasContainer = ref(null);
const maxPreviewHeight = 520;
const containerWidth = ref(700);
const MAX_VENTUZ_OUTPUTS = 7;
const gpuOptions = ref(buildDefaultGpuOptions());
const displayCount = ref(MAX_VENTUZ_OUTPUTS);
const displayOptions = computed(() => buildDisplayOptions(displayCount.value));
const fullscreenOptions = [
  { title: 'Windowed', value: 'FloatingWindow' },
  { title: 'Fullscreen (Borderless)', value: 'Fullscreen' },
  { title: 'Exclusive Fullscreen', value: 'ExclusiveFullscreen' },
];

const canvasWidth = computed(() => deluge.config?.canvas?.x || 3840);
const canvasHeight = computed(() => deluge.config?.canvas?.y || 2160);

const scale = computed(() => {
  if (!canvasWidth.value || !canvasHeight.value) {
    return 1;
  }
  const widthScale = containerWidth.value / canvasWidth.value;
  const heightScale = maxPreviewHeight / canvasHeight.value;
  const next = Math.min(widthScale || 1, heightScale || 1);
  return next > 0 ? next : 1;
});

const previewWidth = computed(() =>
  Math.max(canvasWidth.value * scale.value, 200)
);
const previewHeight = computed(() =>
  Math.max(canvasHeight.value * scale.value, 200)
);

const selectedOutput = computed(
  () => outputs.value.find((item) => item.tempId === selectedId.value) || null
);

function generateTempId() {
  if (globalThis.crypto?.randomUUID) {
    return globalThis.crypto.randomUUID();
  }
  return `hw-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function mapOutputs(response) {
  return (response.outputs || []).map((output, index) => {
    const gpuIndex = clampToOptions(
      output.gpu_index ?? index,
      gpuOptions.value
    );
    const displayIndex = clampToOptions(
      output.display_index ?? index,
      displayOptions.value
    );
    return {
      ...output,
      tempId: generateTempId(),
      rotation: output.rotation ?? 0,
      name: output.name || `GPU Output ${index + 1}`,
      gpu_index: gpuIndex,
      display_index: displayIndex,
      jack: `o${displayIndex}`,
      fullscreen_mode: output.fullscreen_mode || 'FloatingWindow',
      width: output.width ?? canvasWidth.value,
      height: output.height ?? canvasHeight.value,
      left: output.left ?? 0,
      top: output.top ?? 0,
    };
  });
}

async function loadOutputs() {
  loading.value = true;
  error.value = '';
  try {
    const response = await fetchHardwareOutputs();
    outputs.value = mapOutputs(response);
    selectedId.value = outputs.value[0]?.tempId || null;
  } catch (err) {
    console.error(err);
    error.value =
      err?.detail || err?.message || 'Unable to load hardware outputs.';
  } finally {
    loading.value = false;
  }
}

async function loadCapabilities() {
  try {
    const response = await fetchHardwareCapabilities();
    const gpuList = Array.isArray(response?.gpu_options)
      ? response.gpu_options
      : [];
    gpuOptions.value = buildGpuOptionsFromResponse(gpuList);
    displayCount.value = clampDisplayCount(response?.display_count);
  } catch (err) {
    console.error('Hardware capability detection failed:', err);
    gpuOptions.value = buildDefaultGpuOptions();
    displayCount.value = MAX_VENTUZ_OUTPUTS;
  } finally {
    clampOutputsToCapabilities();
  }
}

function selectOutput(id) {
  selectedId.value = id;
}

function outputLabel(output) {
  return (outputs.value.indexOf(output) + 1).toString();
}

function addOutput() {
  const index = outputs.value.length + 1;
  const defaultGpu = clampToOptions(0, gpuOptions.value);
  const defaultDisplay = clampToOptions(0, displayOptions.value);
  const newOutput = {
    tempId: generateTempId(),
    name: `GPU Output ${index}`,
    gpu_index: defaultGpu,
    display_index: defaultDisplay,
    jack: `o${defaultDisplay}`,
    width: canvasWidth.value,
    height: canvasHeight.value,
    left: 0,
    top: 0,
    rotation: 0,
    fullscreen_mode: 'FloatingWindow',
  };
  outputs.value.push(newOutput);
  selectedId.value = newOutput.tempId;
}

function removeOutput(id) {
  outputs.value = outputs.value.filter((output) => output.tempId !== id);
  if (selectedId.value === id) {
    selectedId.value = outputs.value[0]?.tempId || null;
  }
}

function updateContainerWidth() {
  const width = canvasContainer.value?.getBoundingClientRect()?.width;
  if (width) {
    containerWidth.value = width;
  }
}

function onDrag(x, y) {
  if (!selectedOutput.value) return;
  selectedOutput.value.left = Math.round(x / scale.value);
  selectedOutput.value.top = Math.round(y / scale.value);
}

function onResize(x, y, width, height) {
  if (!selectedOutput.value) return;
  selectedOutput.value.left = Math.round(x / scale.value);
  selectedOutput.value.top = Math.round(y / scale.value);
  selectedOutput.value.width = Math.max(1, Math.round(width / scale.value));
  selectedOutput.value.height = Math.max(1, Math.round(height / scale.value));
}

async function saveOutputs() {
  if (outputs.value.length === 0) {
    error.value = 'Add at least one output before saving.';
    return;
  }
  saving.value = true;
  error.value = '';
  try {
    const payload = outputs.value.map((output, index) => ({
      gpu_index: clampToOptions(output.gpu_index ?? index, gpuOptions.value),
      display_index: clampToOptions(
        output.display_index ?? index,
        displayOptions.value
      ),
      fullscreen_mode: output.fullscreen_mode || 'FloatingWindow',
      width: Math.max(1, Number(output.width) || canvasWidth.value),
      height: Math.max(1, Number(output.height) || canvasHeight.value),
      left: Number(output.left) || 0,
      top: Number(output.top) || 0,
      rotation: Number(output.rotation) || 0,
    }));
    const response = await saveHardwareOutputs(payload);
    outputs.value = mapOutputs(response);
    selectedId.value = outputs.value[0]?.tempId || null;
  } catch (err) {
    console.error(err);
    error.value =
      err?.detail || err?.message || 'Unable to save hardware outputs.';
  } finally {
    saving.value = false;
  }
}

function clampDisplayCount(value) {
  const num = Number(value);
  if (Number.isNaN(num) || num <= 0) {
    return MAX_VENTUZ_OUTPUTS;
  }
  return Math.min(MAX_VENTUZ_OUTPUTS, Math.max(1, num));
}

function clampNumber(value) {
  const num = Number(value);
  if (Number.isNaN(num)) {
    return 0;
  }
  return Math.min(MAX_VENTUZ_OUTPUTS - 1, Math.max(0, num));
}

function getOptionValues(items) {
  return (items || [])
    .map((item) => Number(item?.value))
    .filter(
      (value) =>
        Number.isInteger(value) && value >= 0 && value < MAX_VENTUZ_OUTPUTS
    )
    .sort((a, b) => a - b);
}

function clampToOptions(value, items) {
  const bounded = clampNumber(value);
  const validValues = getOptionValues(items);
  if (validValues.includes(bounded)) {
    return bounded;
  }
  return validValues[0] ?? bounded;
}

function buildDisplayOptions(count) {
  const safeCount = clampDisplayCount(count);
  return Array.from({ length: safeCount }, (_, index) => ({
    title: `Display ${index + 1}`,
    value: index,
  }));
}

function formatGpuLabel(index, name, isPrimary) {
  const ordinal = index + 1;
  const descriptor = name?.trim() || `GPU ${ordinal}`;
  return isPrimary
    ? `GPU ${ordinal} — ${descriptor} (Primary)`
    : `GPU ${ordinal} — ${descriptor}`;
}

function buildDefaultGpuOptions() {
  return Array.from({ length: MAX_VENTUZ_OUTPUTS }, (_, index) => ({
    title: formatGpuLabel(index, `GPU ${index + 1}`, index === 0),
    value: index,
  }));
}

function buildGpuOptionsFromResponse(entries) {
  if (!Array.isArray(entries) || !entries.length) {
    return buildDefaultGpuOptions();
  }
  const deduped = [];
  for (const entry of entries) {
    const normalizedIndex = clampNumber(entry?.index);
    if (deduped.some((item) => item.value === normalizedIndex)) {
      continue;
    }
    const label =
      entry?.label?.trim() ||
      formatGpuLabel(normalizedIndex, entry?.name, entry?.is_primary);
    deduped.push({
      title: label,
      value: normalizedIndex,
    });
    if (deduped.length >= MAX_VENTUZ_OUTPUTS) {
      break;
    }
  }
  return deduped.length ? deduped : buildDefaultGpuOptions();
}

function clampOutputsToCapabilities() {
  if (!outputs.value?.length) {
    return;
  }
  outputs.value = outputs.value.map((output, index) => {
    const nextGpu = clampToOptions(output.gpu_index ?? index, gpuOptions.value);
    const nextDisplay = clampToOptions(
      output.display_index ?? index,
      displayOptions.value
    );
    return {
      ...output,
      gpu_index: nextGpu,
      display_index: nextDisplay,
      jack: `o${nextDisplay}`,
    };
  });
}

watch(
  () => gpuOptions.value,
  () => clampOutputsToCapabilities(),
  { deep: true }
);

watch(
  () => displayOptions.value,
  () => clampOutputsToCapabilities(),
  { deep: true }
);

onMounted(async () => {
  await loadCapabilities();
  await loadOutputs();
  updateContainerWidth();
  window.addEventListener('resize', updateContainerWidth, { passive: true });
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateContainerWidth);
});
</script>
<style scoped>
@import './config.css';
@import 'vue-draggable-resizable/style.css';

.hardware-body {
  display: flex;
  gap: 32px;
  flex-wrap: wrap;
}

.canvas-column {
  flex: 1 1 60%;
  min-width: 320px;
}

.sidebar-column {
  flex: 1 1 35%;
  min-width: 280px;
}

.section-heading {
  font-weight: 600;
  margin-bottom: 12px;
}

.canvas-wrapper {
  background: rgba(0, 0, 0, 0.05);
  padding: 16px;
  border-radius: 8px;
  min-height: 260px;
}

.canvas-area {
  background: #101010;
  border-radius: 6px;
  position: relative;
  overflow: hidden;
  margin: 0 auto;
}

.hardware-output-draggable {
  border: 2px solid rgba(255, 255, 255, 0.25);
  background: rgba(33, 150, 243, 0.1);
  transition:
    border-color 0.15s ease,
    background 0.15s ease,
    box-shadow 0.15s ease;
}

.hardware-output-draggable--selected {
  border-color: rgba(33, 150, 243, 0.95);
  background: rgba(33, 150, 243, 0.25);
}

.canvas-loading,
.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 240px;
  flex-direction: column;
  color: rgba(255, 255, 255, 0.7);
}

.output-chip {
  background: transparent;
  border: 0;
  border-radius: 6px;
  padding: 8px;
  color: white;
  font-size: 12px;
  line-height: 1.2;
  user-select: none;
}

.output-chip--selected {
  font-weight: 600;
}

.output-name {
  font-weight: 600;
}

.output-sub {
  opacity: 0.8;
}

.outputs-list {
  max-height: 260px;
  overflow-y: auto;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.02);
}

.outputs-list__item--selected {
  background-color: rgba(33, 150, 243, 0.1);
}

.details-card {
  margin-top: 24px;
  display: grid;
  gap: 8px;
}

.config-footer {
  display: flex;
  margin-top: 16px;
}

.config-header {
  display: flex;
  align-items: center;
  font-weight: bold;
  font-size: 20px;
}
</style>
